import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useInAppBack from '../hooks/useInAppBack';
import { scrollTop } from '../utils/utilities';

const AppBarContext = createContext({
  actionsSlot: null,
  traySlot: null,
  pageSlot: null,
  pageMode: false,
  pageSticky: false,
  setActionsSlot: () => {},
  setTraySlot: () => {},
  setPageSlot: () => {},
  setPageMode: () => {},
  setPageSticky: () => {},
  goBack: () => {},
  markBack: () => {},
  routeMotion: null,
});

export function AppBarProvider({ children }) {
  const [actionsSlot, setActionsSlot] = useState(null);
  const [traySlot, setTraySlot] = useState(null);
  const [pageSlot, setPageSlot] = useState(null);
  const [pageMode, setPageMode] = useState(false);
  const [pageSticky, setPageSticky] = useState(false);
  const goBack = useInAppBack();
  const history = useHistory();
  const location = useLocation();
  const route = useRef({ pathname: location.pathname, motion: null });

  // Which way the last route change went, for the bar's contents to arrive from: null until the
  // first one, so nothing slides on load. Worked out in render, since a PageBar mounting with the new
  // route has to read it on its first render.
  if (location.pathname !== route.current.pathname) {
    route.current = { pathname: location.pathname, motion: history.action === 'POP' ? 'back' : 'forward' };
  }

  const routeMotion = route.current.motion;

  // Going back always lands at the top of the page. The browser's own restoration is off, since
  // Safari applies it after the page renders and would put the old position back over ours.
  const backPending = useRef(false);
  const lastPathname = useRef(location.pathname);
  const markBack = useCallback(() => {
    backPending.current = true;
  }, []);

  useEffect(() => {
    if (!('scrollRestoration' in window.history)) return;
    window.history.scrollRestoration = 'manual';

    return () => {
      window.history.scrollRestoration = 'auto';
    };
  }, []);

  useEffect(() => {
    // A hash step inside the same page (a service moving between songs) does its own scrolling
    const leftPage = location.pathname !== lastPathname.current;
    lastPathname.current = location.pathname;

    if (backPending.current || (leftPage && history.action === 'POP')) scrollTop(undefined, false);
    backPending.current = false;
  }, [location, history]);

  const value = useMemo(
    () => ({
      actionsSlot,
      traySlot,
      pageSlot,
      pageMode,
      pageSticky,
      setActionsSlot,
      setTraySlot,
      setPageSlot,
      setPageMode,
      setPageSticky,
      goBack,
      markBack,
      routeMotion,
    }),
    [actionsSlot, traySlot, pageSlot, pageMode, pageSticky, goBack, markBack, routeMotion],
  );

  return <AppBarContext.Provider value={value}>{children}</AppBarContext.Provider>;
}

export const useAppBar = () => useContext(AppBarContext);
